<template>
  <el-cascader 
    ref="areaCascader" 
    v-model="selectedValue" 
    :options="cascaderOptions" 
    :props="finalCascaderProps"
    @change="handleAreaChange" 
    @visible-change="onVisibleChange"    
    placeholder="請選擇地區" 
    :style="`width: ${width}`"     
    :show-all-levels="true"
  />
</template>

<script>
import { handleError } from '../utils/notify';

export default {
  name: 'DistSelection',
  
  props: {
    value: {
      type: Array,
      default: () => ['全部', '全部']
    },
    width: {
      type: String,
      default: '200px'
    }
  },
  
  data() {
    return {
      selectedValue: [],  // 改為空陣列，避免與 props 衝突
      districtCache: {}, // 新增：緩存已加載的區域資料
      areaList: [
        { area: "全部", cities: ["全部"] },
        { area: "北部", cities: ["臺北市", "新北市", "桃園市", "新竹市", "新竹縣", "宜蘭縣", "基隆市"] },
        { area: "中部", cities: ["臺中市", "彰化縣", "苗栗縣", "南投縣", "雲林縣"] },
        { area: "南部", cities: ["高雄市", "臺南市", "嘉義市", "嘉義縣", "屏東縣"] },
        { area: "東部", cities: ["花蓮縣", "臺東縣", "澎湖縣", "金門縣", "連江縣"] }
      ],
      cascaderProps: {
        value: 'value',
        label: 'label',
        children: 'children',
        lazy: true,
        lazyLoad: this.lazyLoadDistrict,
        // expandTrigger: 'click',
        changeOnSelect: true, // 允許選擇任意層級的值
        checkStrictly: false, // 預設只能選擇葉節點
        emitPath: true // 回傳完整路徑陣列
      }
    };
  },
  
  computed: {

    finalCascaderProps() {
      // 偵測是否為行動端或平板
      const isMobileOrTablet = typeof window !== 'undefined' && 
        (/iPad|iPhone|iPod|android|Macintosh/i.test(navigator.userAgent) && ('ontouchend' in document));
      
      return {
        ...this.cascaderProps,
        // PC 版用 click，行動版/iPad 自動切換成 hover（在 iOS 上 hover 等於觸控點一下）
        expandTrigger: isMobileOrTablet ? 'hover' : 'click'
      };
    },


    cascaderOptions() {
      // console.log('cascaderOptions 正在重新計算...');
      // console.log('當前 districtCache:', this.districtCache);
      
      const options = [];
      
      // 處理全部選項
      options.push({
        value: '全部',
        label: '全部',
        children: [{ value: '全部', label: '全部', leaf: true }]
      });
      
      // 處理各區域
      this.areaList.filter(item => item.area !== '全部').forEach(area => {
        const children = area.cities.map(city => {
          // 若快取中已有該城市的行政區資料，直接注入 children，讓 cascader 無需懶加載就能找到節點
          if (this.districtCache[city]) {
            return {
              value: city,
              label: city,
              children: [
                { value: '全部', label: '全部', leaf: true },
                ...this.districtCache[city].map(d => ({ value: d, label: d, leaf: true }))
              ]
            };
          }
          // 尚未快取時仍保持懶加載模式
          return {
            value: city,
            label: city,
            leaf: false
          };
        });
        
        options.push({
          value: area.area,
          label: area.area,
          children: children
        });
      });
      
      // console.log('cascaderOptions 計算完成，options:', options);
      return options;
    }
  },
  
  watch: {
    value: {
      async handler(newVal) {
        // console.log('DistSelection - watch.value 觸發:', newVal);
        
        if (JSON.stringify(this.selectedValue) !== JSON.stringify(newVal)) {
          this.selectedValue = newVal || [];
          
          // 如果新值包含三層，預先加載第三層數據
          if (newVal && newVal.length === 3) {
            // console.log('檢測到三層選擇，預加載:', newVal[1]);
            await this.preloadDistrictData(newVal[1]);
          }
        }
      },
      immediate: true,  // 新增：立即執行一次
      deep: true
    },
    selectedValue: {
      handler(newVal) {
        // 如果是第三層選擇「全部」的情況，只發出前兩層
        if (newVal && newVal.length === 3 && newVal[2] === '全部') {
          const processedValue = newVal.slice(0, 2);
          // 避免循環觸發
          if (JSON.stringify(this.value) !== JSON.stringify(processedValue)) {
            this.$emit('input', processedValue);
          }
        } else {
          if (JSON.stringify(this.value) !== JSON.stringify(newVal)) {
            this.$emit('input', newVal);
          }
        }
      },
      deep: true
    }
  },
  
  async mounted() {
    // console.log('DistSelection mounted, props.value:', this.value);
    // console.log('DistSelection mounted, selectedValue:', this.selectedValue);
  },

  beforeDestroy() {
    // 清理工作
  },
  
  methods: {
    onVisibleChange(visible) {
      // 僅在選單打開且是行動端/iPad 時執行
      const isMobileOrTablet = typeof window !== 'undefined' && 
        (/iPad|iPhone|iPod|android|Macintosh/i.test(navigator.userAgent) && ('ontouchend' in document));

      if (visible && isMobileOrTablet) {
        // 定義一個動態綁定事件的函式，只要有滑動或點擊就重新檢查（應對懶加載產生的新節點）
        const bindLeafNodes = () => {
          this.$nextTick(() => {
            const nodes = document.querySelectorAll('.el-cascader-node');
            
            nodes.forEach(node => {
              // 檢查是否為最後一層（葉子節點）
              const isLeaf = node.classList.contains('is-selectable') || !node.querySelector('.el-icon-arrow-right');
              
              if (isLeaf && !node.classList.contains('has-touch-bound')) {
                node.classList.add('has-touch-bound');
                
                // 核心改動：直接對最後一層節點同時監聽 touchend
                node.addEventListener('touchend', (e) => {
                  // 阻止原生 iOS 點擊判定延遲與雙擊
                  e.preventDefault(); 
                  e.stopPropagation();
                  
                  // 觸發 Element UI 內部的點擊選取邏輯
                  node.click(); 
                }, { passive: false });
              }
            });
          });
        };

        // 1. 選單剛打開時先跑一次（處理第一、二層）
        bindLeafNodes();

        // 2. 關鍵：因為是 lazy 懶加載，第二層點擊後才會長出第三層
        // 我們直接監聽外層選單的觸控點擊，只要使用者點了任何一列，等一下下（API加載完）就重新綁定最後一層
        const popper = this.$refs.areaCascader?.$refs?.popper;
        const menuEl = popper || document.querySelector('.el-cascader__dropdown');
        
        if (menuEl && !menuEl.classList.contains('has-lazy-bound')) {
          menuEl.classList.add('has-lazy-bound');
          
          // 當在選單內點擊任何東西（比如點了縣市展開行政區），過 200ms 等 API 渲染完 DOM 後立刻追蹤新節點
          menuEl.addEventListener('touchend', () => {
            setTimeout(bindLeafNodes, 250); 
          });
        }
      }
    },

    // 處理清空事件
    // handleClear() {
    //   console.log('DistSelection - 清空選擇');
    //   // 通知父組件設置為 ['全部', '全部']
    //   const defaultValue = ['全部', '全部'];
    //   this.$emit('input', defaultValue);
    //   this.$emit('area-change', []);
    // },

    // 預加載區域資料（用於初始值包含第三層時）
    async preloadDistrictData(cityName) {
      // console.log('preloadDistrictData 被調用，城市:', cityName);
      
      if (!cityName || cityName === '全部') {
        // console.log('城市名稱無效，跳過預加載');
        return;
      }
      
      // 檢查快取
      if (this.districtCache[cityName]) {
        // console.log(`使用快取的 ${cityName} 區域資料:`, this.districtCache[cityName]);
        
        // 強制重新渲染
        this.$forceUpdate();
        
        // 等待 DOM 更新
        await this.$nextTick();
        
        // 手動觸發 cascader 內部節點初始化
        if (this.$refs.areaCascader) {
          const currentValue = [...this.selectedValue];
          this.selectedValue = [];
          await this.$nextTick();
          this.selectedValue = currentValue;
          await this.$nextTick();
          // console.log('已重新設置 selectedValue (使用快取):', this.selectedValue);
        }
        return;
      }
      
      // console.log('開始從 API 加載區域資料...');
      
      try {
        const qry = {
          "Type": "District",
          "City": cityName
        };
        
        const res = await this.$api.apimgrs.getAddress(qry);
        const districts = res.data || [];
        
        // 使用 Vue.set 確保響應式更新
        this.$set(this.districtCache, cityName, districts);
        
        // console.log(`已預加載 ${cityName} 的區域資料：`, districts);
        // console.log('當前 districtCache:', this.districtCache);
        
        // 等待 DOM 更新
        await this.$nextTick();
        
        // 強制重新渲染以觸發 cascaderOptions 重新計算
        this.$forceUpdate();
        await this.$nextTick();
        
        // 手動觸發 cascader 內部節點初始化
        if (this.$refs.areaCascader) {
          const cascader = this.$refs.areaCascader;
          // console.log('嘗試手動初始化 cascader 節點...');
          
          // 強制 cascader 重新計算節點
          const currentValue = [...this.selectedValue];
          this.selectedValue = [];
          await this.$nextTick();
          this.selectedValue = currentValue;
          await this.$nextTick();
          
          // console.log('已重新設置 selectedValue:', this.selectedValue);
        }
        
      } catch (error) {
        console.error('預加載區域資料失敗：', error);
      }
    },

    // 懶加載區域資料（只在第三層使用懶加載）
    async lazyLoadDistrict(node, resolve) {
      //console.log('懶加載節點：', node);
      
      // 第一層（區域）展開時，不需要加載
      if (node.level === 1) {
        resolve([]);
        return;
      } 
      
      // 第二層（縣市）展開時，一次性加載該城市的所有區域（不真正懶加載）
      if (node.level === 2) {
        const cityName = node.value;
        if (!cityName || cityName === '全部') {
          resolve([]);
          return;
        }
        
        // 檢查快取
        if (this.districtCache[cityName]) {
          // console.log(`使用快取的 ${cityName} 區域資料`);
          const districts = this.districtCache[cityName];
          const districtOptions = [
            {
              value: '全部',
              label: '全部',
              leaf: true
            },
            ...districts.map(district => ({
              value: district,
              label: district,
              leaf: true
            }))
          ];
          resolve(districtOptions);
          return;
        }
        
        this.$store.dispatch("loadingHandler", true);
        
        try {
          const qry = {
            "Type": "District",
            "City": cityName
          };

          //console.log('查詢參數：', qry);
          
          const res = await this.$api.apimgrs.getAddress(qry);
          const districts = res.data || [];

          // 存入快取
          this.districtCache[cityName] = districts;

          //console.log('獲取的區域資料：', districts);
          
          // 添加「全部」選項在最前面
          const districtOptions = [
            {
              value: '全部',
              label: '全部',
              leaf: true
            },
            ...districts.map(district => ({
              value: district,
              label: district,
              leaf: true
            }))
          ];
          
          resolve(districtOptions);
          
        } catch (error) {
          handleError(error, '獲取區域資料失敗');
          resolve([]);
        } finally {
          this.$store.dispatch("loadingHandler", false);
        }
      } else {
        resolve([]);
      }
    },

    // 處理區域變更
    handleAreaChange(values) {
      //console.log('DistSelection - 選擇的區域（完整路徑）：', values);
      
      // 如果第一層選擇「全部」，則回傳空陣列
      if (values && values.length > 0 && values[0] === '全部') {
        this.$emit('area-change', []);
        return;
      }
      
      // 檢查是否為第三層選擇「全部」的情況
      let processedValues = [...values];
      
      // 如果是三層結構，且第三層是「全部」，則只保留前兩層
      if (values.length === 3 && values[2] === '全部') {
        processedValues = values.slice(0, 2);
        //console.log('第三層選擇全部，僅回傳縣市：', processedValues);
        
        // 更新 selectedValue，但僅在內部處理，不觸發 watch
        this.$nextTick(() => {
          // 保留原始值以供顯示，但向外部發送處理後的值
          this.selectedValue = values;
        });
      }
      
      // 發送變更事件給父組件
      this.$emit('area-change', processedValues);
      
      // 自動關閉下拉選單
      this.$nextTick(() => {
        if (this.$refs.areaCascader) {
          try {
            this.$refs.areaCascader.toggleDropDownVisible(false);
          } catch (e) {
            // console.log('使用備用關閉方法');
            const inputEl = this.$refs.areaCascader.$el.querySelector('.el-input__inner');
            if (inputEl) {
              inputEl.blur();
            }
          }
        }
      });
    }
  }
};
</script>

<style scoped>
/* 基本樣式，讓 cascader 看起來美觀 */
::v-deep .el-cascader {
  width: 100%;
  border-radius: 4px;
}

::v-deep .el-cascader:hover {
  border-color: #409EFF;
}

::v-deep .el-cascader-menu {
  min-width: 160px;
}

::v-deep .el-cascader-node {
  padding: 10px 20px;
}

::v-deep .el-cascader-node.is-active {
  color: #409EFF;
  font-weight: bold;
}

::v-deep .el-cascader-node:hover {
  background-color: #f5f7fa;
}


/* 1. 確保所有節點與下拉選單列表都不會觸發雙擊縮放延遲 */
::v-deep .el-cascader-node,
::v-deep .el-cascader-menu__list {
  touch-action: manipulation !important;
  -webkit-tap-highlight-color: transparent;
}

/* 2. 針對最後一層（葉子節點）的特殊處理 */
::v-deep .el-cascader-node.is-selectable {
  /* 允許觸控直接操作 */
  touch-action: manipulation !important;
}

/* 3. 核心修正：讓觸控直接點擊到內部的標籤文字，避開外層容器的事件阻擋 */
::v-deep .el-cascader-node__label {
  pointer-events: auto !important;
  width: 100%;
  height: 100%;
  display: block;
}
</style>